import { useCallback, useEffect, useRef } from "react";

interface UseSwipeOptions {
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  onSwipeMove?: (deltaX: number) => void;
  onSwipeEnd?: () => void;
  threshold?: number;
  maxTranslate?: number;
  velocityThreshold?: number;
  disabled?: boolean;
}

interface UseSwipeReturn {
  ref: (element: HTMLElement | null) => void;
}
export const useSwipe = ({
  onSwipeRight,
  onSwipeLeft,
  onSwipeMove,
  onSwipeEnd,
  threshold = 80,
  velocityThreshold = 0.5,
  maxTranslate = 120,
  disabled = false,
}: UseSwipeOptions): UseSwipeReturn => {
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const currentX = useRef(0);
  const elementRef = useRef<HTMLElement | null>(null);
  const hasTriggeredRef = useRef(false);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (disabled) return;

      const touch = e.touches[0];
      if (!touch) return;

      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();
      currentX.current = 0;
      hasTriggeredRef.current = false;

      // Reset transform
      if (elementRef.current) {
        elementRef.current.style.transition = "none";
        elementRef.current.style.transform = "translateX(0px)";
      }
      onSwipeMove?.(0);
    },
    [disabled, onSwipeMove],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (disabled || !elementRef.current) return;

      const touch = e.touches[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Only allow horizontal swipe
      if (Math.abs(deltaY) > Math.abs(deltaX)) return;

      // Only allow swipe if there's a handler for that direction
      if (deltaX > 0 && !onSwipeRight) return;
      if (deltaX < 0 && !onSwipeLeft) return;

      currentX.current = deltaX;

      // Apply transform with constraint
      const constrainedX =
        deltaX > 0 ? Math.min(deltaX, maxTranslate) : Math.max(deltaX, -maxTranslate);
      elementRef.current.style.transform = `translateX(${constrainedX}px)`;
      onSwipeMove?.(constrainedX);

      // Prevent default to stop scrolling when swiping
      if (Math.abs(deltaX) > 10) {
        e.preventDefault();
      }
    },
    [disabled, onSwipeRight, onSwipeLeft, onSwipeMove, maxTranslate],
  );

  const handleTouchEnd = useCallback(() => {
    if (disabled || !elementRef.current) return;

    const deltaX = currentX.current;
    const deltaTime = Date.now() - touchStartTime.current;
    const velocity = Math.abs(deltaX) / deltaTime;

    // Check if swipe threshold is met for right swipe
    const shouldTriggerRight = deltaX >= threshold || (deltaX > 0 && velocity >= velocityThreshold);

    // Check if swipe threshold is met for left swipe
    const shouldTriggerLeft = deltaX <= -threshold || (deltaX < 0 && velocity >= velocityThreshold);

    if (shouldTriggerRight && onSwipeRight && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      onSwipeRight();
    } else if (shouldTriggerLeft && onSwipeLeft && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      onSwipeLeft();
    }

    // Animate back to original position
    if (elementRef.current) {
      elementRef.current.style.transition = "transform 0.3s ease";
      elementRef.current.style.transform = "translateX(0px)";
    }

    // Clean up
    touchStartX.current = 0;
    touchStartY.current = 0;
    touchStartTime.current = 0;
    currentX.current = 0;
    onSwipeEnd?.();
  }, [disabled, onSwipeRight, onSwipeLeft, threshold, velocityThreshold, onSwipeEnd]);

  // Attach event listeners with passive: false to enable preventDefault
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    element.addEventListener("touchstart", handleTouchStart, { passive: false });
    element.addEventListener("touchmove", handleTouchMove, { passive: false });
    element.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const ref = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
  }, []);

  return { ref };
};
